import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { QRCodeSVG } from 'qrcode.react';
import {
  AlertCircle, QrCode, Copy, Download, Share2, HelpCircle,
  MessageCircle, Send, Camera, Mail, Loader2, Link2, Info, Zap, CheckCircle,
  ExternalLink, ShieldCheck
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/services/api';
import EmailChannelSetup from '@/components/channels/EmailChannelSetup';
import QRConnectModal from '@/components/chatbot/QRConnectModal';
// iter131: per-platform channel cards live in their own files. The parent
// tab now just plumbs shared state (channels/setChannels/saveChannel/…) and
// composes the cards, so adding a new channel (LinkedIn, Signal, …) is a
// one-file add instead of a diff in a 1,100-line monolith.
import WhatsAppChannel from '@/components/chatbot/channels/WhatsAppChannel';
import FacebookChannel from '@/components/chatbot/channels/FacebookChannel';
import InstagramChannel from '@/components/chatbot/channels/InstagramChannel';
import TelegramChannel from '@/components/chatbot/channels/TelegramChannel';

/**
 * Channels tab content extracted from ChatbotDetailPage.jsx.
 * Renders the QR code, integration help guide and all social-channel
 * connection cards (WhatsApp, Facebook, Instagram, LinkedIn, Telegram, Signal).
 */
export const ChannelsTab = ({
  chatbotId,
  chatbot,
  API_URL,
  socialChannelsEnabled,
  featureAccess,
  channels,
  setChannels,
  savingChannel,
  setSavingChannel,
  loadChannels,
  saveChannel,
  disconnectChannel,
}) => {
  const [qrModal, setQrModal] = useState({ open: false, platform: null, label: null });
  const openQR = (platform, label) => setQrModal({ open: true, platform, label });
  // iter128.1 — per-platform gates. Falls back to master socialChannelsEnabled
  // for legacy plans that only set the aggregate flag.
  const platformEnabled = (key) => {
    const v = featureAccess?.[key];
    if (v === undefined) return socialChannelsEnabled;
    return v !== false;
  };
  const platforms = {
    whatsapp: platformEnabled('whatsapp_channel'),
    facebook: platformEnabled('facebook_channel'),
    instagram: platformEnabled('instagram_channel'),
    telegram: platformEnabled('telegram_channel'),
  };
  const disabledPlatforms = Object.entries(platforms)
    .filter(([, v]) => !v)
    .map(([k]) => k.charAt(0).toUpperCase() + k.slice(1));
  const anyDisabled = disabledPlatforms.length > 0;
  return (
    <>
          {/* iter128.1: per-platform disabled banner. Only shows when at least
              one social platform is unavailable on the user's plan. */}
          {anyDisabled && (
            <Card className="bg-amber-50 border-amber-200 shadow-sm" data-testid="social-channels-disabled-banner">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                    <AlertCircle className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-amber-800">
                      {disabledPlatforms.length === 4 ? 'Social Channels Not Available' : `${disabledPlatforms.join(', ')} not available on your plan`}
                    </h4>
                    <p className="text-sm text-amber-700">
                      Your current plan does not include {disabledPlatforms.join(', ')}. Upgrade to connect {disabledPlatforms.length === 1 ? 'this channel' : 'these channels'}.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* QR Code Section */}
          <Card className="bg-white border-slate-200 shadow-sm" data-testid="qr-code-section">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-slate-900">
                <QrCode className="w-5 h-5 text-blue-600" />
                Quick Connect via QR Code
              </CardTitle>
              <CardDescription>
                Generate a QR code for this AI agent. Customers can scan it to instantly start a conversation -- perfect for storefronts, business cards, flyers, and social media posts.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col md:flex-row items-start gap-6">
                <div className="bg-white p-4 rounded-xl border-2 border-slate-200 shadow-sm" data-testid="qr-code-display">
                  <QRCodeSVG 
                    value={`${API_URL}/chat/${chatbotId}`}
                    size={180}
                    bgColor="#ffffff"
                    fgColor="#1e293b"
                    level="H"
                    includeMargin={true}
                    id="chatbot-qr-code"
                  />
                </div>
                <div className="flex-1 space-y-4">
                  <div>
                    <Label className="text-xs text-slate-500 uppercase tracking-wider">Chat Link</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Input 
                        value={`${API_URL}/chat/${chatbotId}`}
                        readOnly 
                        className="text-sm font-mono bg-slate-50"
                        data-testid="qr-chat-link"
                      />
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => {
                          navigator.clipboard.writeText(`${API_URL}/chat/${chatbotId}`);
                          toast.success('Chat link copied!');
                        }}
                        data-testid="qr-copy-link-btn"
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const svg = document.getElementById('chatbot-qr-code');
                        if (!svg) return;
                        const svgData = new XMLSerializer().serializeToString(svg);
                        const canvas = document.createElement('canvas');
                        canvas.width = 400; canvas.height = 400;
                        const ctx = canvas.getContext('2d');
                        const img = new Image();
                        img.onload = () => {
                          ctx.fillStyle = '#ffffff';
                          ctx.fillRect(0, 0, 400, 400);
                          ctx.drawImage(img, 0, 0, 400, 400);
                          const a = document.createElement('a');
                          a.download = `harmonix-qr-${chatbotId}.png`;
                          a.href = canvas.toDataURL('image/png');
                          a.click();
                        };
                        img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
                      }}
                      data-testid="qr-download-btn"
                    >
                      <Download className="w-4 h-4 mr-1.5" />
                      Download PNG
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const text = `Chat with our AI assistant: ${API_URL}/chat/${chatbotId}`;
                        if (navigator.share) {
                          navigator.share({ title: 'Chat with our AI', text, url: `${API_URL}/chat/${chatbotId}` });
                        } else {
                          navigator.clipboard.writeText(text);
                          toast.success('Share link copied!');
                        }
                      }}
                      data-testid="qr-share-btn"
                    >
                      <Share2 className="w-4 h-4 mr-1.5" />
                      Share
                    </Button>
                  </div>
                  <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-sm text-blue-800">
                    <p className="font-medium mb-1">How to use:</p>
                    <ul className="space-y-1 text-xs text-blue-700">
                      <li>Print and display at your storefront or reception</li>
                      <li>Add to business cards, brochures, or flyers</li>
                      <li>Share on Instagram, Facebook, or WhatsApp stories</li>
                      <li>Include in email signatures for instant chat access</li>
                    </ul>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Channel Setup Help Guide */}
          <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-blue-900 flex items-center gap-2 text-lg">
                <HelpCircle className="w-5 h-5" />
                Channel Integration Guide
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Accordion type="single" collapsible className="w-full">
                {/* WhatsApp Setup Guide */}
                <AccordionItem value="whatsapp-guide" className="border-blue-200">
                  <AccordionTrigger className="text-blue-800 hover:text-blue-900">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded bg-[#25D366] flex items-center justify-center">
                        <MessageCircle className="w-4 h-4 text-white" />
                      </div>
                      WhatsApp Business Setup
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-700 space-y-3">
                    <div className="bg-white p-4 rounded-lg border border-blue-100">
                      <h5 className="font-semibold mb-2">Steps to get WhatsApp Business API:</h5>
                      <ol className="list-decimal list-inside space-y-2 text-sm">
                        <li>Go to <a href="https://developers.facebook.com/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Meta for Developers</a></li>
                        <li>Create a new app or select existing app</li>
                        <li>Add &quot;WhatsApp&quot; product to your app</li>
                        <li>Go to WhatsApp → Getting Started</li>
                        <li>Copy your <strong>Phone Number ID</strong> and <strong>WhatsApp Business Account ID</strong></li>
                        <li>Generate a permanent access token from Business Settings</li>
                      </ol>
                      <div className="mt-3 p-2 bg-amber-50 rounded text-amber-800 text-xs">
                        <strong>Note:</strong> WhatsApp Business API requires a verified business. Free tier allows 1,000 conversations/month.
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Facebook Messenger Setup Guide */}
                <AccordionItem value="facebook-guide" className="border-blue-200">
                  <AccordionTrigger className="text-blue-800 hover:text-blue-900">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded bg-[#1877F2] flex items-center justify-center">
                        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                      </div>
                      Facebook Messenger Setup
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-700 space-y-3">
                    <div className="bg-white p-4 rounded-lg border border-blue-100">
                      <h5 className="font-semibold mb-2">Steps to connect Facebook Messenger:</h5>
                      <ol className="list-decimal list-inside space-y-2 text-sm">
                        <li>Go to <a href="https://developers.facebook.com/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Meta for Developers</a></li>
                        <li>Create or select your app</li>
                        <li>Add &quot;Messenger&quot; product</li>
                        <li>Connect your Facebook Page</li>
                        <li>Copy the <strong>Page ID</strong> from your Facebook Page settings</li>
                        <li>Generate a Page Access Token with messaging permissions</li>
                        <li>Set up webhooks for receiving messages</li>
                      </ol>
                      <div className="mt-3 p-2 bg-green-50 rounded text-green-800 text-xs">
                        <strong>Cost:</strong> Free to use for business pages
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Telegram Setup Guide */}
                <AccordionItem value="telegram-guide" className="border-blue-200">
                  <AccordionTrigger className="text-blue-800 hover:text-blue-900">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded bg-[#0088cc] flex items-center justify-center">
                        <Send className="w-4 h-4 text-white" />
                      </div>
                      Telegram Bot Setup (Easiest!)
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-700 space-y-3">
                    <div className="bg-white p-4 rounded-lg border border-blue-100">
                      <h5 className="font-semibold mb-2">Steps to create a Telegram Bot:</h5>
                      <ol className="list-decimal list-inside space-y-2 text-sm">
                        <li>Open Telegram and search for <a href="https://t.me/botfather" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">@BotFather</a></li>
                        <li>Send <code className="bg-slate-100 px-1 rounded">/newbot</code> command</li>
                        <li>Choose a name for your bot (e.g., &quot;Harmonix Support&quot;)</li>
                        <li>Choose a username ending in &quot;bot&quot; (e.g., &quot;harmonix_support_bot&quot;)</li>
                        <li>BotFather will give you a <strong>Bot Token</strong> - copy it</li>
                        <li>Paste the token here and click Connect</li>
                      </ol>
                      <div className="mt-3 p-2 bg-green-50 rounded text-green-800 text-xs">
                        <strong>Cost:</strong> Completely FREE with unlimited messages!
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Instagram Setup Guide */}
                <AccordionItem value="instagram-guide" className="border-blue-200">
                  <AccordionTrigger className="text-blue-800 hover:text-blue-900">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 flex items-center justify-center">
                        <Camera className="w-4 h-4 text-white" />
                      </div>
                      Instagram DM Setup
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-700 space-y-3">
                    <div className="bg-white p-4 rounded-lg border border-blue-100">
                      <h5 className="font-semibold mb-2">Steps to connect Instagram:</h5>
                      <ol className="list-decimal list-inside space-y-2 text-sm">
                        <li>Convert your Instagram to a <strong>Business Account</strong></li>
                        <li>Connect it to a Facebook Page</li>
                        <li>Go to <a href="https://developers.facebook.com/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Meta for Developers</a></li>
                        <li>Add &quot;Instagram&quot; product to your app</li>
                        <li>Go to Instagram → Basic Display or Instagram Graph API</li>
                        <li>Generate an access token with messaging permissions</li>
                        <li>Copy your <strong>Instagram Business Account ID</strong></li>
                      </ol>
                      <div className="mt-3 p-2 bg-amber-50 rounded text-amber-800 text-xs">
                        <strong>Requirement:</strong> Must have a Facebook Page linked to Instagram Business Account
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* LinkedIn Setup Guide */}
                <AccordionItem value="linkedin-guide" className="border-blue-200">
                  <AccordionTrigger className="text-blue-800 hover:text-blue-900">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded bg-[#0A66C2] flex items-center justify-center">
                        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                      </div>
                      LinkedIn Company Page Setup
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-700 space-y-3">
                    <div className="bg-white p-4 rounded-lg border border-blue-100">
                      <h5 className="font-semibold mb-2">Steps to connect LinkedIn:</h5>
                      <ol className="list-decimal list-inside space-y-2 text-sm">
                        <li>Go to <a href="https://www.linkedin.com/developers/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">LinkedIn Developers</a></li>
                        <li>Create an app and select your Company Page</li>
                        <li>Request access to Marketing API products</li>
                        <li>Add OAuth 2.0 redirect URL for your domain</li>
                        <li>Get your <strong>Client ID</strong> and <strong>Client Secret</strong></li>
                        <li>Generate access token with messaging permissions</li>
                        <li>Find your <strong>Company Page ID</strong> in your page&apos;s admin settings</li>
                      </ol>
                      <div className="mt-3 p-2 bg-blue-50 rounded text-blue-800 text-xs">
                        <strong>Note:</strong> LinkedIn API access requires app review for production use
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </CardContent>
          </Card>

          {socialChannelsEnabled && (
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-slate-900 flex items-center gap-2">
                <Share2 className="w-5 h-5 text-blue-600" />
                Social Media Channels
              </CardTitle>
              <p className="text-sm text-slate-600">
                Connect your social media accounts to enable AI auto-replies for your customers
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Email Channel — added per user request (Feb 2026). Lives inside the
                  agent's Channels tab so it sits alongside WhatsApp / FB / IG. */}
              <div className="p-4 rounded-xl border border-slate-200 bg-gradient-to-r from-white to-blue-50/30">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                      <Mail className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900">Email</h4>
                      <p className="text-sm text-slate-500">Auto-reply to customer emails (Gmail or IMAP)</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white border border-slate-200 rounded-lg p-3">
                  <EmailChannelSetup chatbotId={chatbotId} />
                </div>
              </div>

              {/* WhatsApp Business */}
              {platforms.whatsapp && (
                <WhatsAppChannel
                  chatbotId={chatbotId}
                  channels={channels}
                  setChannels={setChannels}
                  savingChannel={savingChannel}
                  saveChannel={saveChannel}
                  disconnectChannel={disconnectChannel}
                  openQR={openQR}
                />
              )}

              {/* Facebook Pages */}
              {platforms.facebook && (
                <FacebookChannel
                  chatbotId={chatbotId}
                  channels={channels}
                  loadChannels={loadChannels}
                  disconnectChannel={disconnectChannel}
                  openQR={openQR}
                />
              )}

              {/* Instagram Business */}
              {platforms.instagram && (
                <InstagramChannel
                  chatbotId={chatbotId}
                  channels={channels}
                  loadChannels={loadChannels}
                  disconnectChannel={disconnectChannel}
                  openQR={openQR}
                />
              )}

              {/* LinkedIn */}
              <div className="p-4 rounded-xl border border-slate-200 bg-gradient-to-r from-white to-blue-50/20 opacity-70">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-[#0A66C2] flex items-center justify-center">
                      <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900">LinkedIn Company Page</h4>
                      <p className="text-sm text-slate-500">Auto-reply to LinkedIn messages</p>
                    </div>
                  </div>
                  <Badge className="bg-slate-100 text-slate-500">
                    Coming Soon
                  </Badge>
                </div>
                <p className="text-sm text-slate-500">LinkedIn messaging integration will be available in a future update. We&apos;re working on getting partner program access.</p>
              </div>

              {/* Telegram Bot */}
              {platforms.telegram && (
                <TelegramChannel
                  chatbotId={chatbotId}
                  chatbot={chatbot}
                  channels={channels}
                  setChannels={setChannels}
                  savingChannel={savingChannel}
                  setSavingChannel={setSavingChannel}
                  saveChannel={saveChannel}
                  disconnectChannel={disconnectChannel}
                  loadChannels={loadChannels}
                />
              )}

              {/* Signal */}
              <div className="p-4 rounded-xl border border-slate-200 bg-gradient-to-r from-white to-indigo-50/30 opacity-70">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-[#3A76F0] flex items-center justify-center">
                      <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm-.036 4.8a7.2 7.2 0 0 1 7.133 8.16 7.2 7.2 0 0 1-5.88 5.88 7.2 7.2 0 0 1-3.36 0 7.163 7.163 0 0 1-2.52-1.14l-2.64.84.84-2.64a7.163 7.163 0 0 1-1.14-2.52 7.2 7.2 0 0 1 7.56-8.58z"/></svg>
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900">Signal</h4>
                      <p className="text-sm text-slate-500">Auto-reply to Signal messages</p>
                    </div>
                  </div>
                  <Badge className="bg-slate-100 text-slate-500">
                    Coming Soon
                  </Badge>
                </div>
                <p className="text-sm text-slate-500">Signal messaging integration will be available in a future update. We&apos;re working on Signal Bot API support.</p>
              </div>

              {/* Info Box */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-amber-800">
                    <strong>How it works:</strong> Once you connect a channel, your AI agent will automatically respond to messages from potential clients on that platform. It uses the knowledge base and personality you&apos;ve configured to answer questions, qualify leads, and book appointments.
                    <ul className="list-disc list-inside mt-2 text-amber-700 space-y-0.5">
                      <li><strong>WhatsApp</strong> requires API credentials from Meta Business Suite</li>
                      <li><strong>Facebook</strong> &amp; <strong>Instagram</strong> connect via OAuth - just sign in and select your page</li>
                      <li><strong>Telegram</strong> - create a bot via @BotFather, paste the token, then activate the webhook</li>
                    </ul>
                    <p className="mt-2 font-medium">AI Takeover Behavior:</p>
                    <ul className="list-disc list-inside text-amber-700 space-y-0.5">
                      <li>The AI agent automatically manages all conversations on connected channels</li>
                      <li>When you (the human) start replying, the AI steps back and observes</li>
                      <li>Once you stop replying for a few minutes, the AI resumes handling the conversation</li>
                      <li>Lead information is collected politely and naturally during conversations</li>
                    </ul>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          )}
          <QRConnectModal
            open={qrModal.open}
            platform={qrModal.platform}
            label={qrModal.label}
            chatbotId={chatbotId}
            onOpenChange={(v) => setQrModal((s) => ({ ...s, open: v }))}
            onConnected={() => {
              // Reload channel list so the connected badge appears immediately
              // after the phone finishes OAuth.
              try { loadChannels?.(); } catch { /* noop */ }
            }}
          />
    </>
  );
};

export default ChannelsTab;
